import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, User, Mail, Phone, CheckCircle, XCircle, Clock as ClockIcon, Filter, MoreHorizontal } from 'lucide-react';
import { notification, Modal, Dropdown, Menu } from 'antd';
import { useTranslation } from 'react-i18next';

const AppointmentsTab = () => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today'); // today, yesterday, tomorrow, all
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, confirmed, cancelled, completed
  const VITE_SERVER_URL = import.meta.env.VITE_SERVER_URL || 'https://easyrdv.onrender.com';

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const appointmentsRes = await axios.get(`${VITE_SERVER_URL}/api/appointments`);
      // Vérifier la structure de la réponse et gérer les différents cas
      const appointments = appointmentsRes.data?.data?.appointments || appointmentsRes.data?.appointments || [];
      setAppointments(appointments);
    } catch (error) {
      console.error('Erreur lors du chargement des rendez-vous:', error);
      // En cas d'erreur, initialiser avec un tableau vide
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour filtrer les rendez-vous par date
  const getFilteredAppointments = () => {
    let filtered = appointments;

    // Filtre par date
    if (dateFilter !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      filtered = filtered.filter(appointment => {
        const appointmentDate = new Date(appointment.date);
        appointmentDate.setHours(0, 0, 0, 0);

        switch (dateFilter) {
          case 'today':
            return appointmentDate.getTime() === today.getTime();
          case 'tomorrow':
            return appointmentDate.getTime() === tomorrow.getTime();
          case 'yesterday':
            return appointmentDate.getTime() === yesterday.getTime();
          default:
            return true;
        }
      });
    }

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(appointment => appointment.status === statusFilter.toUpperCase());
    }

    return filtered;
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await axios.patch(`${VITE_SERVER_URL}/api/appointments/${appointmentId}/status`, {
        status
      });
      fetchAppointments(); // Recharger les données
      notification.success({
        message: t('dashboard.appointments.statusUpdateSuccess.message'),
        description: t('dashboard.appointments.statusUpdateSuccess.description', { status: status === 'CONFIRMED' ? t('dashboard.appointments.statusLabel.confirmed') : status === 'CANCELLED' ? t('dashboard.appointments.statusLabel.cancelled') : status === 'COMPLETED' ? t('dashboard.appointments.statusLabel.completed') : status }),
        placement: 'topRight',
        style: {
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(80, 36, 255, 0.12)',
          background: 'linear-gradient(90deg, #6366f1 0%, #a21caf 100%)',
          color: '#fff',
          fontWeight: 500,
        },
        duration: 2.5,
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      notification.error({
        message: t('dashboard.appointments.statusUpdateError.message'),
        description: t('dashboard.appointments.statusUpdateError.description'),
        placement: 'topRight',
        style: {
          borderRadius: 12,
          boxShadow: '0 4px 24px rgba(255, 36, 36, 0.12)',
          background: 'linear-gradient(90deg, #ef4444 0%, #be185d 100%)',
          color: '#fff',
          fontWeight: 500,
        },
        duration: 2.5,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'text-green-600 bg-green-100';
      case 'PENDING':
        return 'text-yellow-600 bg-yellow-100';
      case 'CANCELLED':
        return 'text-red-600 bg-red-100';
      case 'COMPLETED':
        return 'text-blue-600 bg-blue-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return <CheckCircle className="w-4 h-4" />;
      case 'PENDING':
        return <ClockIcon className="w-4 h-4" />;
      case 'CANCELLED':
        return <XCircle className="w-4 h-4" />;
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDateLabel = (filter) => {
    switch (filter) {
      case 'today':
        return t('dashboard.appointments.dateFilter.today');
      case 'tomorrow':
        return t('dashboard.appointments.dateFilter.tomorrow');
      case 'yesterday':
        return t('dashboard.appointments.dateFilter.yesterday');
      case 'all':
        return t('dashboard.appointments.dateFilter.all');
      default:
        return t('dashboard.appointments.dateFilter.all');
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'all':
        return t('dashboard.appointments.statusFilter.all');
      case 'pending':
        return t('dashboard.appointments.statusFilter.pending');
      case 'confirmed':
        return t('dashboard.appointments.statusFilter.confirmed');
      default:
        return t('dashboard.appointments.statusFilter.all');
    }
  };

  const filteredAppointments = getFilteredAppointments();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filtres */}
      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
        <div className="flex items-center space-x-2 mb-3 sm:mb-4">
          <Filter className="w-4 h-4 text-gray-500" />
          <h3 className="text-xs sm:text-sm font-medium text-gray-700">{t('dashboard.appointments.filters')}</h3>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Filtre par date */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('dashboard.appointments.date')}</label>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              {['today', 'tomorrow', 'yesterday', 'all'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setDateFilter(filter)}
                  className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md transition-colors ${
                    dateFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t(`dashboard.appointments.dateFilter.${filter}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Filtre par statut */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">{t('dashboard.appointments.status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
            >
              <option value="all">{t('dashboard.appointments.statusFilter.all')}</option>
              <option value="pending">{t('dashboard.appointments.statusFilter.pending')}</option>
              <option value="confirmed">{t('dashboard.appointments.statusFilter.confirmed')}</option>
            </select>
          </div>
        </div>

        {/* Résumé des filtres */}
        <div className="mt-3 text-xs sm:text-sm text-gray-600">
          {filteredAppointments.length} {t('dashboard.appointments.found')}
          {dateFilter !== 'all' && ` ${t('dashboard.appointments.for')} ${t(`dashboard.appointments.dateFilter.${dateFilter}`)}`}
          {statusFilter !== 'all' && ` ${t('dashboard.appointments.withStatus')} "${t(`dashboard.appointments.statusFilter.${statusFilter}`)}"`}
        </div>
      </div>

      {/* Liste des rendez-vous */}
      {filteredAppointments.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('dashboard.appointments.noAppointments')}</h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            {dateFilter !== 'all' || statusFilter !== 'all' 
              ? t('dashboard.appointments.noMatch')
              : t('dashboard.appointments.noAny')
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredAppointments.map((appointment) => (
            <div key={appointment.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-lg font-medium text-gray-900 truncate">
                      {appointment.customerName}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="truncate">{appointment.customerEmail}</span>
                      </div>
                      {appointment.customerPhone && (
                        <div className="flex items-center space-x-1">
                          <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{appointment.customerPhone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="text-left sm:text-right">
                    <p className="text-xs sm:text-sm font-medium text-gray-900">
                      {formatDate(appointment.date)}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {appointment.time}
                    </p>
                  </div>
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                    {getStatusIcon(appointment.status)}
                    <span>
                      {appointment.status === 'PENDING' && t('dashboard.appointments.statusLabel.pending')}
                      {appointment.status === 'CONFIRMED' && t('dashboard.appointments.statusLabel.confirmed')}
                      {appointment.status === 'CANCELLED' && t('dashboard.appointments.statusLabel.cancelled')}
                      {appointment.status === 'COMPLETED' && t('dashboard.appointments.statusLabel.completed')}
                    </span>
                  </div>
                </div>
              </div>
              
              {appointment.notes && (
                <div className="mt-3 p-2 sm:p-3 bg-gray-50 rounded-md">
                  <p className="text-xs sm:text-sm text-gray-600">{appointment.notes}</p>
                </div>
              )}
              {(appointment.status === 'PENDING') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    className="px-3 py-1 rounded-md bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors"
                    onClick={() => updateAppointmentStatus(appointment.id, 'CONFIRMED')}
                  >
                    {t('dashboard.appointments.actions.confirm')}
                  </button>
                  <button
                    className="px-3 py-1 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors"
                    onClick={() => {
                      Modal.confirm({
                        title: t('dashboard.appointments.actions.cancelTitle'),
                        okText: t('dashboard.appointments.actions.yes'),
                        okType: 'danger',
                        cancelText: t('dashboard.appointments.actions.no'),
                        centered: true,
                        onOk: () => updateAppointmentStatus(appointment.id, 'CANCELLED', appointment.status),
                      });
                    }}
                  >
                    {t('dashboard.appointments.actions.cancel')}
                  </button>
                </div>
              )}

              {(appointment.status === 'CONFIRMED') && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item key="cancel" onClick={() => {
                          Modal.confirm({
                            title: t('dashboard.appointments.actions.cancelTitle'),
                            okText: t('dashboard.appointments.actions.yes'),
                            okType: 'danger',
                            cancelText: t('dashboard.appointments.actions.no'),
                            centered: true,
                            onOk: () => updateAppointmentStatus(appointment.id, 'CANCELLED', appointment.status),
                          });
                        }}>
                          {t('dashboard.appointments.actions.cancel')}
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={['click']}
                  >
                    <button className="p-2 rounded-full hover:bg-gray-200 transition-colors" aria-label="Plus d'actions">
                      <MoreHorizontal className="w-5 h-5 text-gray-600" />
                    </button>
                  </Dropdown>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AppointmentsTab; 